import { createTruck, updateTruck, createTrailer, updateTrailer } from "./units.api.js";
import { main } from "../../main/mainpage.js";
import { deleteSignalBooster } from "./repeaters.api.js";
import { updateRepeaters, createSignalBoosters } from "./handlerepeaterdata.js";
import { manualPairSensor } from "../sensors/sensors.api.js";

let signalBoosterCounter = 0;
let newTruck = false;
let isSubmitting = false;

let originalRepeaters = [];

const PAIRING_MODE = {
  AUTO: 'auto',
  MANUAL: 'manual'
};

const getPairingMode = () => {
  return document.getElementById('pairingmode')?.value || PAIRING_MODE.AUTO;
};

const buildWheelId = (position, axle, unitNumber) => {
  return `${position}${String(axle).padStart(2, '0')}${unitNumber}`;
};

const getUnitNumberForWheelId = (fleetType, fleetAsset, data) => {
  if (/truck/i.test(fleetType)) return 0;

  return Number(
    data?.trailerNumber ??
    fleetAsset?.trailerNumber ??
    fleetAsset?.trailerOrder ??
    1
  );
};

const normaliseApiUnitType = (fleetType) => {
  return /truck/i.test(fleetType) ? 'Truck' : 'Trailer';
};

const addManualPairingHeaderRow = () => {
  const headerRow = document.createElement('div');
  headerRow.classList.add('row', 'manual-pairing-header-row', 'mt-3', 'mb-1', 'small', 'fw-bold');

  const headings = [
    'Location Sensor - Left',
    'Outer Wheel Sensor',
    'Inner Wheel Sensor',
    'Wheels Config - Left',
    'Location Sensor - Right',
    'Inner Wheel Sensor',
    'Outer Wheel Sensor',
    'Wheels Config - Right'
  ];

  headings.forEach(text => {
    const col = document.createElement('div');
    col.classList.add('col');
    col.textContent = text;
    headerRow.appendChild(col);
  });

  return headerRow;
};

// Named listener function for "input" event
const handleNumberOfAxles = (e) => {
  e.stopImmediatePropagation();
  const axlesContainer = document.getElementById("axlescontainer");
  axlesContainer.innerHTML = "";
  addAxlesStep();
};

// Named listener functions for buttons
const handleAddSignalBoostersClick = (e) => {
  e.stopImmediatePropagation();
  addSignalBooster();
};

const createWheelTypeSelect = (id, cssClass, value = 'Single') => {
  const select = document.createElement('select');
  select.id = id;
  select.classList.add('form-control', cssClass);

  const singleOption = document.createElement('option');
  singleOption.value = 'Single';
  singleOption.textContent = 'Single';

  const doubleOption = document.createElement('option');
  doubleOption.value = 'Double';
  doubleOption.textContent = 'Double';

  select.append(singleOption, doubleOption);
  select.value = value || 'Single';

  return select;
};

const createTextInput = (id, placeholder, value = '', extraClasses = []) => {
  const input = document.createElement('input');
  input.type = 'text';
  input.id = id;
  input.classList.add('form-control', ...extraClasses);
  input.placeholder = placeholder;

  if (value !== undefined && value !== null) {
    input.value = value;
  }

  return input;
};

const capitalize = (value) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const updateManualInnerWheelState = (axleNumber, side) => {
  const wheelType = document.getElementById(`${side}repeatertype-${axleNumber}`)?.value;
  const innerInput = document.getElementById(`${side}innerwheelsensorid-${axleNumber}`);

  if (!innerInput) return;

  const isSingle = wheelType === 'Single';

  innerInput.disabled = isSingle;

  if (isSingle) {
    innerInput.value = '';
    innerInput.placeholder = `${capitalize(side)} Inner Disabled`;
  } else {
    innerInput.placeholder = `Wheel Sensor Id`;
  }
};

const wrapCol = (element, colClass = 'col-md-2') => {
  const div = document.createElement('div');
  div.classList.add(colClass);
  div.appendChild(element);
  return div;
};

const submitManualPairings = async (fleetAsset, data, axleData, fleetType) => {
  const unitType = normaliseApiUnitType(fleetType);
  const unitId = fleetAsset?.Id || fleetAsset?.unitId || data?.unitId;

  if (!unitId) {
    return {
      success: false,
      message: 'Manual pairing failed: unitId is missing.'
    };
  }

  const unitNumber = getUnitNumberForWheelId(fleetType, fleetAsset, data);
  const requests = [];

  axleData.forEach(axle => {
    const axleNo = axle.axle;

    if (axle.left?.outerSensorId) {
      requests.push({
        sensorId: axle.left.outerSensorId,
        wheelId: buildWheelId(1, axleNo, unitNumber),
        unitType,
        unitId
      });
    }

    if (axle.left?.wheel === 'Double' && axle.left?.innerSensorId) {
      requests.push({
        sensorId: axle.left.innerSensorId,
        wheelId: buildWheelId(2, axleNo, unitNumber),
        unitType,
        unitId
      });
    }

    if (axle.right?.wheel === 'Double' && axle.right?.innerSensorId) {
      requests.push({
        sensorId: axle.right.innerSensorId,
        wheelId: buildWheelId(3, axleNo, unitNumber),
        unitType,
        unitId
      });
    }

    if (axle.right?.outerSensorId) {
      requests.push({
        sensorId: axle.right.outerSensorId,
        wheelId: buildWheelId(4, axleNo, unitNumber),
        unitType,
        unitId
      });
    }
  });

  if (requests.length === 0) {
    return {
      success: true,
      message: 'No manual wheel sensors entered.'
    };
  }

  const results = [];

  for (const request of requests) {
    const result = await manualPairSensor(request);
    results.push({
      request,
      result
    });

    if (!result.success) {
      return {
        success: false,
        message: result.message,
        failedRequest: request,
        results
      };
    }
  }

  return {
    success: true,
    message: `${requests.length} manual wheel sensor pairing(s) saved.`,
    results
  };
};

const emitUnitConfigSaved = (unit, fleetAsset, data, fleetType, extraDetail = {}) => {
  window.dispatchEvent(new CustomEvent('pressuretrack:unit-config-saved', {
    detail: {
      unitId: fleetAsset?.Id || fleetAsset?.unitId || unit?.unitId || null,
      imei: fleetAsset?.IMEI || data?.IMEI || unit?.imei || null,
      fleetType,
      unitType: normaliseApiUnitType(fleetType),
      ...extraDetail
    }
  }));
};

// Form submit listener
const handleFormSubmit = (unit, fleetAsset, data, axleData, repeatersToUpdate = [], signalBoosters, fleetType, pairingMode) => {

  if (isSubmitting) return;
  isSubmitting = true;

  // Function to handle asset creation/updation and call updateRepeaters
  const processFleetAsset = async (promise) => {
    try {
      const result = await promise;
      fleetAsset = result;

      const unitHeaderName = document.getElementById(`unitheadername${unit?.unitId || unit?.imei}`);
      if (unitHeaderName) {
        unitHeaderName.innerText = data.name || unit?.imei;
      }

      if (repeatersToUpdate.length > 0) {
        await updateRepeaters(fleetAsset, repeatersToUpdate);
      }

      let manualPairingRequests = [];

      if (pairingMode === PAIRING_MODE.MANUAL) {
        const manualResult = await submitManualPairings(fleetAsset, data, axleData, fleetType);

        if (!manualResult.success) {
          alert(manualResult.message || 'Manual pairing failed.');
          console.error('Manual pairing failed:', manualResult);
          return;
        }

        manualPairingRequests = manualResult.results
          ?.map(item => item.request)
          ?.filter(Boolean) || [];

        console.log(manualResult.message);
      }

      await createSignalBoosters(fleetAsset, signalBoosters);
      fleetAsset.axleData = axleData;
      closeConfigForm();
      emitUnitConfigSaved(unit, fleetAsset, data, fleetType, {
        manualPairingRequests
      });

      console.log("Fleet asset processed successfully:", result);

    } catch (error) {
      console.error("Error processing fleet asset:", error);
      alert('The unit could not be saved. Please check the console for details.');
    } finally {
      isSubmitting = false;
    }
  };

  // Determine which promise to handle based on the logic
  if (unit && unit.imei && newTruck) {
    processFleetAsset(createTruck(unit.customerId, data));
  } else if (unit && unit.imei && !newTruck) {
    processFleetAsset(updateTruck(fleetAsset, data));
  } else {
    if (fleetAsset) {
      processFleetAsset(updateTrailer(fleetAsset, data));
    } else {
      processFleetAsset(createTrailer(data));
    }
  }
};

const resetUnitConfigForm = () => {

  document.getElementById('unitname').value = '';
  document.getElementById("numberofaxles").value = '';
  document.getElementById('numberofsparewheels').value = '';
  document.getElementById('unitconfigform').reset();

  const addSignalBoostersButton = document.getElementById('addsignalboostersbtn');
  addSignalBoostersButton.style.display = 'block';
  document.getElementById('addsignalboostersstep').classList.add('d-none');
  document.getElementById('addaxlesstep').classList.add('d-none');

  clearContainer('axlescontainer');
  clearContainer('signalboosterscontainer');

  resetStateVariables();
};

function gatherFormData(imei, fleetAsset, fleetType) {

  const form = document.getElementById('unitconfigform');
  const formData = new FormData(form);

  const pairingMode = formData.get('pairingmode') || PAIRING_MODE.AUTO;

  const isNumericId = (value) => /^\d+$/.test(String(value || '').trim());
  const toNumericId = (value) => Number(String(value || '').trim());

  const numberOfAxles = parseInt(formData.get('numberofaxles'), 10);

  const data = fleetType === 'TRUCK' ? {
    name: formData.get('unitname'),
    axles: numberOfAxles,
    spareWheelSlots: parseInt(formData.get('numberofsparewheels'), 10) || 0,
    IMEI: imei
  } : {
    name: formData.get('unitname'),
    axles: numberOfAxles,
    spareWheelSlots: parseInt(formData.get('numberofsparewheels'), 10) || 0,
    trailerNumber: parseInt(formData.get('trailerorder'), 10)
  };

  const axleData = [];
  const repeatersToUpdate = [];

  let isValid = true;

  if (!numberOfAxles || numberOfAxles < 1) {
    return {
      isValid: false
    };
  }

  for (let i = 1; i <= numberOfAxles; i++) {

    const leftWheel = document.getElementById(`leftrepeatertype-${i}`)?.value;
    const rightWheel = document.getElementById(`rightrepeatertype-${i}`)?.value;

    if (!leftWheel || !rightWheel) {
      isValid = false;
      alert(`Please select the wheel configuration for axle ${i}.`);
      break;
    }

    if (pairingMode === PAIRING_MODE.MANUAL) {
      const leftLocationId = document.getElementById(`leftlocationid-${i}`)?.value.trim();
      const rightLocationId = document.getElementById(`rightlocationid-${i}`)?.value.trim();

      const leftOuterId = document.getElementById(`leftouterwheelsensorid-${i}`)?.value.trim();
      const leftInnerId = document.getElementById(`leftinnerwheelsensorid-${i}`)?.value.trim();
      const rightInnerId = document.getElementById(`rightinnerwheelsensorid-${i}`)?.value.trim();
      const rightOuterId = document.getElementById(`rightouterwheelsensorid-${i}`)?.value.trim();

      if (leftLocationId && !isNumericId(leftLocationId)) {
        isValid = false;
        alert(`Please enter a valid Left Location Sensor Id for axle ${i}.`);
        break;
      }

      if (rightLocationId && !isNumericId(rightLocationId)) {
        isValid = false;
        alert(`Please enter a valid Right Location Sensor Id for axle ${i}.`);
        break;
      }

      if (!isNumericId(leftOuterId)) {
        isValid = false;
        alert(`Please enter a valid Left Outer Wheel Sensor Id for axle ${i}.`);
        break;
      }

      if (leftWheel === 'Double' && !isNumericId(leftInnerId)) {
        isValid = false;
        alert(`Please enter a valid Left Inner Wheel Sensor Id for axle ${i}.`);
        break;
      }

      if (rightWheel === 'Double' && !isNumericId(rightInnerId)) {
        isValid = false;
        alert(`Please enter a valid Right Inner Wheel Sensor Id for axle ${i}.`);
        break;
      }

      if (!isNumericId(rightOuterId)) {
        isValid = false;
        alert(`Please enter a valid Right Outer Wheel Sensor Id for axle ${i}.`);
        break;
      }

      if (leftLocationId) {
        repeatersToUpdate.push({
          Id: toNumericId(leftLocationId),
          unitId: fleetAsset?.Id || fleetAsset?.unitId || null,
          unitType: normaliseApiUnitType(fleetType),
          config: { side: "Left", axle: i, wheel: leftWheel }
        });
      }

      if (rightLocationId) {
        repeatersToUpdate.push({
          Id: toNumericId(rightLocationId),
          unitId: fleetAsset?.Id || fleetAsset?.unitId || null,
          unitType: normaliseApiUnitType(fleetType),
          config: { side: "Right", axle: i, wheel: rightWheel }
        });
      }

      axleData.push({
        axle: i,
        unitId: fleetAsset?.Id || fleetAsset?.unitId || null,
        unitType: normaliseApiUnitType(fleetType),
        //pairingMode: PAIRING_MODE.MANUAL,
        left: {
          locationSensorId: leftLocationId ? toNumericId(leftLocationId) : null,
          wheel: leftWheel,
          outerSensorId: toNumericId(leftOuterId),
          innerSensorId: leftWheel === 'Double' ? toNumericId(leftInnerId) : null
        },
        right: {
          locationSensorId: rightLocationId ? toNumericId(rightLocationId) : null,
          wheel: rightWheel,
          innerSensorId: rightWheel === 'Double' ? toNumericId(rightInnerId) : null,
          outerSensorId: toNumericId(rightOuterId)
        }
      });

      continue;
    }

    const leftId = document.getElementById(`leftlocationid-${i}`)?.value.trim();
    const rightId = document.getElementById(`rightlocationid-${i}`)?.value.trim();

    if (!isNumericId(leftId)) {
      isValid = false;
      alert(`Please enter a valid Left Location Sensor Id for axle ${i}.`);
      break;
    }

    if (!isNumericId(rightId)) {
      isValid = false;
      alert(`Please enter a valid Right Location Sensor Id for axle ${i}.`);
      break;
    }

    axleData.push({
      left: {
        Id: toNumericId(leftId),
        unitId: fleetAsset?.Id || fleetAsset?.unitId || null,
        unitType: normaliseApiUnitType(fleetType),
        config: { side: "Left", axle: i, wheel: leftWheel }
      },
      right: {
        Id: toNumericId(rightId),
        unitId: fleetAsset?.Id || fleetAsset?.unitId || null,
        unitType: normaliseApiUnitType(fleetType),
        config: { side: "Right", axle: i, wheel: rightWheel }
      },
    });
  }

  const signalBoosters = [];
  const signalBoosterRows = document.querySelectorAll('.signalbooster-row');

  signalBoosterRows.forEach((row) => {
    const input = row.querySelector('input');
    const value = input?.value?.trim();

    if (value && isNumericId(value)) {
      const normalisedType = /trailer/i.test(fleetType) ? 'Trailer' :
        /truck/i.test(fleetType) ? 'Truck' : null;

      signalBoosters.push({
        Id: toNumericId(value),
        unitId: fleetAsset?.unitId || fleetAsset?.Id || null,
        unitType: normalisedType
      });
    }
  });

  if (!isValid) {
    return { isValid };
  }

  if (pairingMode === PAIRING_MODE.AUTO) {
    if (!originalRepeaters || originalRepeaters.length === 0) {
      repeatersToUpdate.push(
        ...axleData
          .flatMap(axle => [axle.left, axle.right])
          .filter(rep => !!rep.Id)
      );
    } else {
      const findOriginal = (side, axleNo) =>
        originalRepeaters.find(r =>
          r.config?.side === side &&
          parseInt(r.config?.axle, 10) === axleNo
        );

      axleData.forEach(axle => {
        ["left", "right"].forEach(key => {
          const rep = axle[key];

          if (!rep || !rep.Id) return;

          const orig = findOriginal(rep.config.side, rep.config.axle);

          if (!orig) {
            repeatersToUpdate.push(rep);
            return;
          }

          const origWheel = orig.config?.wheel || "Single";

          if (
            Number(orig.Id) !== Number(rep.Id) ||
            origWheel !== rep.config.wheel
          ) {
            repeatersToUpdate.push(rep);
          }
        });
      });
    }
  }

  return {
    data,
    axleData,
    repeatersToUpdate,
    signalBoosters,
    pairingMode,
    isValid
  };
}

const resetStateVariables = () => {
  isSubmitting = false; // Reset submission state
  newTruck = false;
  signalBoosterCounter = 0;
  document.getElementById('unitconfigform').reset();
};

function closeConfigForm() {
  const modal = document.getElementById('unitconfigmodal');
  if (!modal) {
    console.error('Modal element not found');
    return;
  }

  modal.classList.remove('show');
  modal.style.display = 'none';
  //modal.setAttribute('inert', ''); 

  const backdrop = document.querySelector('.modal-backdrop');
  if (backdrop) {
    backdrop.remove(); // Remove backdrop only if it exists
  } else {
    console.warn("Backdrop not found");
  }

  // Clean up listeners and reset state
  resetUnitConfigForm();
  resetStateVariables();
}

const showSignalBoosters = (fleetAsset) => {
  const signalBoostersContainer = document.getElementById('signalboosterscontainer');
  signalBoostersContainer.innerHTML = ''; // Clear any existing rows

  // Loop through the signalBoosterIds and create rows for each
  fleetAsset.signalBoosterIds.forEach((id, index) => {
    const signalBoosterRow = document.createElement('div');
    signalBoosterRow.classList.add('row', 'signalbooster-row', 'mt-2');
    signalBoosterRow.id = `signalbooster-row-${index + 1}`;

    // Input for Signal Booster ID
    const signalBoosterDiv = document.createElement('div');
    signalBoosterDiv.classList.add('col-md-3');
    const signalBoosterInput = document.createElement('input');
    signalBoosterInput.type = 'text';
    signalBoosterInput.classList.add('form-control');
    signalBoosterInput.placeholder = 'Signal Booster ID';
    signalBoosterInput.value = id; // Prepopulate with the existing ID
    signalBoosterInput.disabled = true; // Disable editing for pre-existing IDs
    signalBoosterDiv.appendChild(signalBoosterInput);

    // Add/Remove Button
    const buttonDiv = document.createElement('div');
    buttonDiv.classList.add('col-md-1', 'd-flex', 'justify-content-center', 'align-items-center');
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.classList.add('btn', 'btn-danger', 'btn-block', 'w-100', 'remove-repeater');
    removeButton.id = `remove-signalbooster-btn-${index + 1}`;
    removeButton.textContent = 'Remove';
    buttonDiv.appendChild(removeButton);

    // Placeholder for additional columns
    const placeHolderDiv = document.createElement('div');
    placeHolderDiv.classList.add('col-md-8');

    // Append all elements to the row
    signalBoosterRow.append(signalBoosterDiv, buttonDiv, placeHolderDiv);
    signalBoostersContainer.appendChild(signalBoosterRow);

    // Attach remove functionality to the remove button
    removeButton.addEventListener('click', () => removeSignalBoosterRow(fleetAsset, removeButton));
  });

  // Add a button for creating a new signal booster
  if (fleetAsset.signalBoosterIds.length > 0) {
    addSignalBoosterRow(fleetAsset); // Ensure a new empty row for additional boosters
  }
};

const getNumberOfAxles = (fleetAsset) => {
  let maxAxle = 0;
  if (fleetAsset.repeaters && fleetAsset.repeaters.length > 0) {
    let trailerRepeaters = fleetAsset.repeaters.filter(repeater => repeater.unitType === 'Trailer');
    trailerRepeaters.forEach(repeater => {
      if (repeater.config && repeater.config.axle) {
        maxAxle = Math.max(maxAxle, repeater.config.axle);
      }
    });
  }
  fleetAsset.axles = maxAxle;
  return fleetAsset.axles;
};

const toggleTrailerOrder = (shouldShow, trailerOrder) => {
  const trailerOrderLabel = document.getElementById('trailerorderlabel');
  const trailerOrderInput = document.getElementById('trailerorder');
  if (shouldShow) {
    trailerOrderInput.value = trailerOrder;
    trailerOrderLabel.style.display = 'block';
    trailerOrderInput.style.display = 'block'; // Show the input
    trailerOrderInput.setAttribute('required', 'required'); // Make it required
  } else {
    trailerOrderLabel.style.display = 'none';
    trailerOrderInput.style.display = 'none'; // Hide the input
    trailerOrderInput.removeAttribute('required'); // Remove the required attribute
  }
};

function addSignalBooster() {
  const unitName = document.getElementById('unitname').value.trim();
  if (unitName) {
    const addSignalBoostersButton = document.getElementById('addsignalboostersbtn');
    addSignalBoostersButton.style.display = 'none';
    addSignalBoosterStep();
  } else {
    alert('Please enter a name for the unit.');
  }
}

function handleAddAxlesClick() {
  const unitName = document.getElementById('unitname').value.trim();
  if (unitName) {
    // const configureAxlesButton = document.getElementById('configureaxlesbtn');
    // configureAxlesButton.style.display = 'none';
    addAxlesStep();
  } else {
    alert('Please enter a name for the unit.');
  }
}

function addSignalBoosterStep() {
  document.getElementById('addsignalboostersstep').classList.remove('d-none');
  addSignalBoosterRow();
}

const addAutoAxleRow = (axleNumber, leftRepeaterData = null, rightRepeaterData = null) => {

  const axleRow = document.createElement('div');
  axleRow.classList.add('row', 'axle-row', 'mt-2');
  axleRow.id = `axle-row-${axleNumber}`;

  const leftLocationSensorInput = createTextInput(
    `leftlocationid-${axleNumber}`,
    `Axle ${axleNumber} - Left Location Id`,
    leftRepeaterData?.Id || '',
    ['left-repeater']
  );

  const leftWheelSelect = createWheelTypeSelect(
    `leftrepeatertype-${axleNumber}`,
    'left-wheeltype',
    leftRepeaterData?.config?.wheel || 'Single'
  );

  const axleSvgDiv = document.createElement('div');
  axleSvgDiv.classList.add('col-md-auto');
  axleSvgDiv.textContent = ' ';

  const rightLocationSensorInput = createTextInput(
    `rightlocationid-${axleNumber}`,
    `Axle ${axleNumber} - Right Location Id`,
    rightRepeaterData?.Id || '',
    ['right-repeater']
  );

  const rightWheelSelect = createWheelTypeSelect(
    `rightrepeatertype-${axleNumber}`,
    'right-wheeltype',
    rightRepeaterData?.config?.wheel || 'Single'
  );

  axleRow.append(
    wrapCol(leftLocationSensorInput, 'col-md-3'),
    wrapCol(leftWheelSelect, 'col-md-2'),
    axleSvgDiv,
    wrapCol(rightLocationSensorInput, 'col-md-3'),
    wrapCol(rightWheelSelect, 'col-md-2')
  );

  return axleRow;
};

const addManualAxleRow = (axleNumber, manualAxleData = null) => {

  const axleRow = document.createElement('div');
  axleRow.classList.add('row', 'axle-row', 'manual-axle-row', 'mt-2', 'gx-2');
  axleRow.id = `axle-row-${axleNumber}`;

  const leftWheelType = manualAxleData?.left?.wheel || 'Single';
  const rightWheelType = manualAxleData?.right?.wheel || 'Single';

  const leftLocationInput = createTextInput(
    `leftlocationid-${axleNumber}`,
    `Location Sensor Id`,
    manualAxleData?.left?.locationSensorId || '',
    ['left-location-sensor']
  );

  const leftOuterInput = createTextInput(
    `leftouterwheelsensorid-${axleNumber}`,
    `Wheel Sensor Id`,
    manualAxleData?.left?.outerSensorId || '',
    ['left-outer-wheel-sensor']
  );

  const leftInnerInput = createTextInput(
    `leftinnerwheelsensorid-${axleNumber}`,
    `Wheel Sensor Id`,
    manualAxleData?.left?.innerSensorId || '',
    ['left-inner-wheel-sensor']
  );

  const leftWheelSelect = createWheelTypeSelect(
    `leftrepeatertype-${axleNumber}`,
    'left-wheeltype',
    leftWheelType
  );

  const rightLocationInput = createTextInput(
    `rightlocationid-${axleNumber}`,
    `Location Sensor Id`,
    manualAxleData?.right?.locationSensorId || '',
    ['right-location-sensor']
  );

  const rightInnerInput = createTextInput(
    `rightinnerwheelsensorid-${axleNumber}`,
    `Wheel Sensor Id`,
    manualAxleData?.right?.innerSensorId || '',
    ['right-inner-wheel-sensor']
  );

  const rightOuterInput = createTextInput(
    `rightouterwheelsensorid-${axleNumber}`,
    `Wheel Sensor Id`,
    manualAxleData?.right?.outerSensorId || '',
    ['right-outer-wheel-sensor']
  );

  const rightWheelSelect = createWheelTypeSelect(
    `rightrepeatertype-${axleNumber}`,
    'right-wheeltype',
    rightWheelType
  );

  leftWheelSelect.addEventListener('change', () => updateManualInnerWheelState(axleNumber, 'left'));
  rightWheelSelect.addEventListener('change', () => updateManualInnerWheelState(axleNumber, 'right'));

  axleRow.append(
    wrapCol(leftLocationInput, 'col'),
    wrapCol(leftOuterInput, 'col'),
    wrapCol(leftInnerInput, 'col'),
    wrapCol(leftWheelSelect, 'col'),
    wrapCol(rightLocationInput, 'col'),
    wrapCol(rightInnerInput, 'col'),
    wrapCol(rightOuterInput, 'col'),
    wrapCol(rightWheelSelect, 'col')
  );

  setTimeout(() => {
    updateManualInnerWheelState(axleNumber, 'left');
    updateManualInnerWheelState(axleNumber, 'right');
  }, 0);

  return axleRow;
};

const addAxleRow = (axleNumber) => {
  const pairingMode = getPairingMode();

  if (pairingMode === PAIRING_MODE.MANUAL) {
    return addManualAxleRow(axleNumber);
  }

  return addAutoAxleRow(axleNumber);
};

const addAxlesStep = () => {

  document.getElementById('addaxlesstep').classList.remove('d-none');

  const axlesContainer = document.getElementById('axlescontainer');
  axlesContainer.innerHTML = '';

  const numberOfAxles = parseInt(document.getElementById("numberofaxles").value, 10);
  const pairingMode = getPairingMode();

  if (!numberOfAxles || numberOfAxles < 1) return;

  if (pairingMode === PAIRING_MODE.MANUAL) {
    axlesContainer.appendChild(addManualPairingHeaderRow());
  }

  for (let i = 1; i <= numberOfAxles; i++) {
    const axleRow = addAxleRow(i);
    axlesContainer.appendChild(axleRow);
  }
};

const showAxlesConfig = (fleetAsset, repeaters) => {
  document.getElementById('addaxlesstep').classList.remove('d-none');

  const axlesContainer = document.getElementById('axlescontainer');
  axlesContainer.innerHTML = '';

  const pairingMode = getPairingMode();

  if (pairingMode === PAIRING_MODE.MANUAL) {
    axlesContainer.appendChild(addManualPairingHeaderRow());
  }

  for (let axle = 1; axle <= fleetAsset.axles; axle++) {

    if (pairingMode === PAIRING_MODE.MANUAL) {
      const manualAxleData = fleetAsset?.manualWheelSensorData?.find(
        item => parseInt(item.axle, 10) === axle
      );

      axlesContainer.appendChild(addManualAxleRow(axle, manualAxleData));
      continue;
    }

    const leftRepeater = repeaters.find(
      rep => rep.config?.side === 'Left' && parseInt(rep.config?.axle, 10) === axle
    );

    const rightRepeater = repeaters.find(
      rep => rep.config?.side === 'Right' && parseInt(rep.config?.axle, 10) === axle
    );

    axlesContainer.appendChild(addAutoAxleRow(axle, leftRepeater, rightRepeater));
  }
};

function addSignalBoosterRow(fleetAsset) {

  // Increment axle counter for the next row
  signalBoosterCounter++;
  const signalBoosterRow = document.createElement('div');
  signalBoosterRow.classList.add('row', 'signalbooster-row', 'mt-2');
  signalBoosterRow.id = `signalbooster-row-${signalBoosterCounter}`; // Assign a unique ID to the repeater row

  // Repeater Input
  const signalBoosterDiv = document.createElement('div');
  signalBoosterDiv.classList.add('col-md-3');
  const signalBoosterInput = document.createElement('input');
  signalBoosterInput.type = 'text';
  signalBoosterInput.classList.add('form-control');
  signalBoosterInput.placeholder = 'Add signal booster';
  signalBoosterDiv.appendChild(signalBoosterInput);

  const buttonDiv = document.createElement('div');
  buttonDiv.classList.add('col-md-1', 'd-flex', 'justify-content-center', 'align-items-center'); //,  'align-items-center'

  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.classList.add('btn', 'btn-success', 'btn-block', 'w-100', 'add-signalbooster');
  addButton.id = `add-signalbooster-btn-${signalBoosterCounter}`; // Unique ID for the add button
  addButton.textContent = 'Add';
  buttonDiv.appendChild(addButton);

  // Repeater Input
  const placeHolderDiv = document.createElement('div');
  placeHolderDiv.classList.add('col-md-8');

  // Append all elements to the axleRow
  signalBoosterRow.append(signalBoosterDiv, buttonDiv, placeHolderDiv);

  // Append axleRow to the container
  document.getElementById('signalboosterscontainer').appendChild(signalBoosterRow);

  addButton.addEventListener('click', handleAddSignalBoosterClick);
}

function handleAddSignalBoosterClick() {
  console.log('Add Button clicked');
  // Change the previous row's add button to a remove button
  const lastRow = document.querySelector(`#signalbooster-row-${signalBoosterCounter}`); // The row before this one
  if (lastRow) {

    const lastButton = lastRow.querySelector('.add-signalbooster');
    if (lastButton) {
      // Convert to "Remove" button
      lastButton.classList.remove('btn-success', 'add-signalbooster');
      lastButton.classList.add('btn-danger', 'remove-repeater');
      lastButton.textContent = 'Remove';

      lastButton.removeEventListener('click', handleAddSignalBoosterClick);
      lastButton.addEventListener('click', () => removeSignalBoosterRow(null, lastButton));
    }
  }

  // Add the new repeater row
  addSignalBoosterRow();
}

const removeSignalBoosterRow = (fleetAsset, buttonElement) => {
  const rowId = buttonElement.id.split('-').pop();
  const rowToRemove = document.getElementById('signalbooster-row-' + rowId);

  if (rowToRemove) {
    // Get the value of the signal booster ID from the input field
    const signalBoosterInput = rowToRemove.querySelector('input[type="text"]');
    const signalBoosterId = signalBoosterInput ? signalBoosterInput.value : null;

    if (signalBoosterId && fleetAsset) {
      // Trigger API call to delete the signal booster ID
      deleteSignalBooster(fleetAsset.customerId, signalBoosterId)
        .then(() => {
          // Remove the row from the DOM after successful deletion
          rowToRemove.parentNode.removeChild(rowToRemove);
          console.log(`Signal booster ID ${signalBoosterId} deleted successfully.`);
        })
        .catch((error) => {
          console.error(`Failed to delete signal booster ID ${signalBoosterId}:`, error);
        });
    } else {
      rowToRemove.parentNode.removeChild(rowToRemove);
      console.warn("No signal booster ID found in the row to remove.");
    }
  } else {
    console.error("Row to remove not found");
  }
};


// const addAxleConfigRow = (axleIndex, lastAxle, leftRepeaterData, rightRepeaterData) => {

//   const axleRow = document.createElement('div');
//   axleRow.classList.add('row', 'axle-row', 'mt-2');
//   axleRow.id = `axle-row-${axleIndex}`; // Assign a unique ID to the axle row

//   // First column: Left Repeater Input (larger column)
//   const leftLocationSensorDiv = document.createElement('div');
//   leftLocationSensorDiv.classList.add('col-md-3');
//   const leftLocationSensorInput = document.createElement('input');
//   leftLocationSensorInput.id = `leftlocationid-${axleIndex}`;
//   leftLocationSensorInput.type = 'text';
//   leftLocationSensorInput.classList.add('form-control', 'left-repeater');
//   leftLocationSensorInput.placeholder = `Axle ${axleIndex} - Left Location Id`;
//   if (leftRepeaterData) {
//     leftLocationSensorInput.value = leftRepeaterData.Id;
//   }
//   leftLocationSensorDiv.appendChild(leftLocationSensorInput);

//   // Second column: Left Wheel Type Select (smaller column)
//   const leftWheelDiv = document.createElement('div');
//   leftWheelDiv.classList.add('col-md-2');
//   const leftWheelSelect = document.createElement('select');
//   leftWheelSelect.id = `leftrepeatertype-${axleIndex}`;
//   leftWheelSelect.classList.add('form-control', 'left-wheeltype');
//   const leftSingleOption = document.createElement('option');
//   leftSingleOption.value = 'Single';
//   leftSingleOption.textContent = 'Single';
//   const leftDoubleOption = document.createElement('option');
//   leftDoubleOption.value = 'Double';
//   leftDoubleOption.textContent = 'Double';

//   leftWheelSelect.append(leftSingleOption, leftDoubleOption);
//   leftWheelDiv.appendChild(leftWheelSelect);

//   if (leftRepeaterData) {
//     leftWheelSelect.value = leftRepeaterData.config.wheel;
//   }

//   // Third column: Axle SVG Placeholder (takes remaining space)
//   const axleSvgDiv = document.createElement('div');
//   axleSvgDiv.classList.add('col-md-auto');
//   axleSvgDiv.textContent = ' ';

//   // Fourth column: Right Repeater Input (larger column)
//   const rightLocationSensorDiv = document.createElement('div');
//   rightLocationSensorDiv.classList.add('col-md-3');
//   const rightLocationSensorInput = document.createElement('input');
//   rightLocationSensorInput.id = `rightlocationid-${axleIndex}`;
//   rightLocationSensorInput.type = 'text';
//   rightLocationSensorInput.classList.add('form-control', 'right-repeater');
//   rightLocationSensorInput.placeholder = `Axle ${axleIndex} - Right Location Id`;
//   if (rightRepeaterData) {
//     rightLocationSensorInput.value = rightRepeaterData.Id;
//   }
//   rightLocationSensorDiv.appendChild(rightLocationSensorInput);

//   // Fifth column: Right Wheel Type Select (smaller column)
//   const rightWheelDiv = document.createElement('div');
//   rightWheelDiv.classList.add('col-md-2');
//   const rightWheelSelect = document.createElement('select');
//   rightWheelSelect.id = `rightrepeatertype-${axleIndex}`;
//   rightWheelSelect.classList.add('form-control', 'right-wheeltype');
//   const rightSingleOption = document.createElement('option');
//   rightSingleOption.value = 'Single';
//   rightSingleOption.textContent = 'Single';
//   const rightDoubleOption = document.createElement('option');
//   rightDoubleOption.value = 'Double';
//   rightDoubleOption.textContent = 'Double';
//   rightWheelSelect.append(rightSingleOption, rightDoubleOption);

//   if (rightRepeaterData) {
//     rightWheelSelect.value = rightRepeaterData.config.wheel;
//   }
//   rightWheelDiv.appendChild(rightWheelSelect);


//   // Sixth column: Button container for add/remove buttons
//   // Add the "Add Axle" button to the new row

//   // Append all elements to the axleRow
//   axleRow.append(leftLocationSensorDiv, leftWheelDiv, axleSvgDiv, rightLocationSensorDiv, rightWheelDiv);

//   // Append axleRow to the container
//   document.getElementById('axlescontainer').appendChild(axleRow);
// };

function clearContainer(containerId) {
  const container = document.getElementById(containerId);
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
}

function attachEvent(element, eventType, callback) {
  element.removeEventListener(eventType, callback);
  element.addEventListener(eventType, callback);
}

export const openUnitConfigForm = (unit, fleetAsset, fleetType, repeaters) => {

  resetUnitConfigForm();

  originalRepeaters = Array.isArray(repeaters)
    ? JSON.parse(JSON.stringify(repeaters))  // deep-ish clone
    : [];

  const imei = unit ? unit.imei : null;
  const unitConfigModalLabel = document.getElementById('unitconfigmodallabel');

  const unitName = document.getElementById('unitname');
  const pairingMode = document.getElementById('pairingmode');
  const numberOfAxles = document.getElementById("numberofaxles");
  const numberOfSpareWheels = document.getElementById('numberofsparewheels');

  const addSignalBoostersButton = document.getElementById('addsignalboostersbtn');
  const unitConfigForm = document.getElementById('unitconfigform');

  // Ensure listeners are only added once
  attachEvent(numberOfAxles, "input", handleNumberOfAxles);
  attachEvent(addSignalBoostersButton, "click", handleAddSignalBoostersClick);

  if (pairingMode) {
    pairingMode.value = fleetAsset?.pairingMode || PAIRING_MODE.AUTO;

    attachEvent(pairingMode, "change", () => {
      const axlesContainer = document.getElementById("axlescontainer");
      axlesContainer.innerHTML = "";

      if (numberOfAxles.value && !isNaN(parseInt(numberOfAxles.value, 10))) {
        if (fleetAsset && fleetAsset.axles > 0) {
          showAxlesConfig(fleetAsset, repeaters || []);
        } else {
          addAxlesStep();
        }
      }
    });
  }

  unitConfigForm.onsubmit = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    const { data, axleData, repeatersToUpdate, signalBoosters, pairingMode, isValid } = gatherFormData(imei, fleetAsset, fleetType); // Adjust this function as necessary
    if (isValid) {
      handleFormSubmit(unit, fleetAsset, data, axleData, repeatersToUpdate, signalBoosters, fleetType, pairingMode); // Call submit logic with validated data
    }
  };

  if ((!imei && fleetAsset) || (imei && !fleetAsset)) {
    newTruck = true;
    unitName.value = fleetAsset?.name || 'Enter Truck Name';
    if (fleetAsset) numberOfAxles.value = fleetAsset?.axles || getNumberOfAxles(fleetAsset);
  }
  if (imei && fleetType === 'TRUCK') {
    unitConfigModalLabel.textContent = `Configure Truck for IMEI ${imei}`;
    unitName.value = fleetAsset?.name || unit?.name || 'Enter Truck Name';
    // sinkIdLabel.style.display = 'block';
    // sinkId.style.display = 'block';  
    //if (repeaters.length) sinkId.value = unit.sinkId || '';

    toggleTrailerOrder(false);
  } else {
    unitConfigModalLabel.textContent = `Configure Trailer`;
    toggleTrailerOrder(true, fleetAsset ? fleetAsset?.trailerNumber || '' : '');
    // sinkIdLabel.style.display = 'none';        
    // sinkId.style.display = 'none';
  }

  fleetAsset && fleetAsset?.axles ? numberOfAxles.value = fleetAsset.axles : numberOfAxles.value = "Enter Number of Axles";

  if (repeaters && repeaters.length && fleetAsset && fleetAsset.axles > 0) {
    showAxlesConfig(fleetAsset, repeaters);
  }

  if (fleetAsset && fleetAsset?.signalBoosterIds?.length > 0) {
    document.getElementById('addsignalboostersstep').classList.remove('d-none');
    document.getElementById('addsignalboostersbtn').style.display = 'none';
    showSignalBoosters(fleetAsset);
  }

  if (fleetAsset) numberOfSpareWheels.value = fleetAsset?.spareWheelSlots ?? "Enter Number of Spare Wheels";
  const unitConfigModal = new bootstrap.Modal(document.getElementById('unitconfigmodal'), {});
  unitConfigModal.show();
};